import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, STATUS_COLORS, ORDER_STATUSES } from "@/lib/pharmacy";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const load = () => api.get("/admin/orders").then((r) => {
    const found = r.data.find((x) => x.id === orderId);
    setO(found); setNewStatus(found?.status || "");
  });
  useEffect(load, [orderId]);

  const updateStatus = async () => {
    if (!newStatus || newStatus === o.status) return;
    try { const { data } = await api.put(`/admin/orders/${orderId}/status`, { status: newStatus }); setO(data); toast.success("Status updated"); }
    catch { toast.error("Failed to update"); }
  };
  const resend = async () => {
    try { const { data } = await api.post(`/admin/orders/${orderId}/resend-email`); toast[data.sent ? "success" : "error"](data.sent ? "Email resent to admin" : "Failed to send"); }
    catch { toast.error("Failed to send"); }
  };

  if (!o) return <div className="text-slate-500">Loading...</div>;
  return (
    <div>
      <div className="text-sm text-slate-500"><Link to="/admin/orders">Orders</Link> / <span className="font-mono">{o.order_number}</span></div>
      <div className="flex flex-wrap justify-between items-start mt-2">
        <div>
          <h1 className="font-display text-3xl font-bold">{o.order_number}</h1>
          <div className="text-sm text-slate-500">{o.created_at.slice(0,16).replace("T"," ")}</div>
        </div>
        <div className="flex gap-2">
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="admin-status-select">
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={updateStatus} className="btn-primary" data-testid="admin-update-status">Update</button>
          <button onClick={resend} className="btn-ghost" data-testid="admin-resend-email"><Mail className="h-4 w-4"/> Resend admin email</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 card-flat p-6">
          <h3 className="font-display font-bold mb-3">Items</h3>
          <ul className="divide-y divide-slate-100">
            {o.items.map((i) => (
              <li key={`${i.product_id}-${i.pack_size}`} className="py-3 flex justify-between text-sm">
                <div>
                  <div className="font-semibold">{i.name}{i.prescription_required && <span className="ml-2 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Rx</span>}</div>
                  <div className="text-xs text-slate-500">{i.generic_name} · {i.strength} · Pack of {i.pack_size} × {i.quantity}</div>
                </div>
                <div className="font-mono">{fmtMoney(i.line_total)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Total pills</span><span className="font-mono">{o.total_pills}</span></div>
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtMoney(o.subtotal)}</span></div>
            <div className="flex justify-between"><span>Discount</span><span className="font-mono">-{fmtMoney(o.discount)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{fmtMoney(o.shipping_cost)}</span></div>
            <div className="flex justify-between font-bold pt-2 border-t"><span>Grand total</span><span className="font-mono">{fmtMoney(o.grand_total)}</span></div>
          </div>
          <div className="mt-4 text-xs flex flex-wrap gap-2">
            <span className={`px-2 py-1 rounded-full ${STATUS_COLORS[o.status]}`}>{o.status}</span>
            {o.payment_method && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">Payment: {o.payment_method}</span>}
            {o.prescription_required && <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700">Rx acknowledged: {o.prescription_ack ? "Yes" : "No"}</span>}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="card-flat p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Customer</div>
            <div className="text-sm">
              <div><strong>{o.shipping.full_name}</strong></div>
              <div className="text-slate-600">{o.shipping.email}</div>
              <div className="text-slate-600">{o.shipping.phone}</div>
              <div className="mt-2 text-slate-600">{o.shipping.address_line}<br/>{o.shipping.city}, {o.shipping.state} {o.shipping.postal_code}<br/>{o.shipping.country}</div>
              {o.shipping.notes && <div className="mt-2 text-xs bg-slate-50 rounded p-2">Notes: {o.shipping.notes}</div>}
            </div>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Status history</div>
            <ol className="space-y-2 text-sm">
              {(o.status_history || []).map((s,idx) => (
                <li key={idx} className="flex justify-between"><span>{s.status}</span><span className="text-xs text-slate-500 font-mono">{s.at.slice(0,16).replace("T"," ")}</span></li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}
